import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { T } from "@/lib/testIds";
import { MessageSquare, Copy, ExternalLink, Search, User } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

export default function SmsTemplates() {
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [rendered, setRendered] = useState({}); // {template_id: text}

  useEffect(() => {
    (async () => {
      try {
        const [t, c] = await Promise.all([
          api.get("/sms-templates"),
          api.get("/clients"),
        ]);
        setTemplates(t.data || []);
        setClients(c.data || []);
        if (c.data && c.data.length > 0) setSelectedClient(c.data[0].id);
      } catch {
        toast.error("Failed to load SMS templates");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Group by category
  const grouped = useMemo(() => {
    const filter = q.trim().toLowerCase();
    const g = {};
    templates.forEach((t) => {
      if (
        filter &&
        !t.name.toLowerCase().includes(filter) &&
        !t.category.toLowerCase().includes(filter) &&
        !t.description.toLowerCase().includes(filter)
      ) return;
      if (!g[t.category]) g[t.category] = [];
      g[t.category].push(t);
    });
    return g;
  }, [templates, q]);

  // Re-render all templates whenever selected client changes
  useEffect(() => {
    if (!selectedClient || templates.length === 0) {
      setRendered({});
      return;
    }
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        templates.map((t) =>
          api
            .post("/sms-templates/render", { template_id: t.id, client_id: selectedClient })
            .then((r) => [t.id, r.data])
            .catch(() => [t.id, null])
        )
      );
      if (cancelled) return;
      const map = {};
      results.forEach(([id, data]) => {
        if (data) map[id] = data;
      });
      setRendered(map);
    })();
    return () => { cancelled = true; };
  }, [selectedClient, templates]);

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("SMS copied — paste into your Messages app");
    } catch {
      toast.info("Copy failed — long-press and select the text");
    }
  };

  const openSms = (client, text) => {
    if (!client?.phone) {
      toast.error("Client has no phone number");
      return;
    }
    // sms: URI opens native SMS composer on iOS/Android with pre-filled body
    const phone = client.phone.replace(/\s/g, "");
    const body = encodeURIComponent(text);
    // iOS uses `&body=`, Android uses `?body=` — combined form works on both
    window.location.href = `sms:${phone}?body=${body}`;
  };

  const currentClient = clients.find((c) => c.id === selectedClient);

  return (
    <div className="px-6 lg:px-10 py-8 max-w-6xl">
      <div className="flex items-center gap-3 mb-2">
        <MessageSquare className="h-6 w-6 text-[#4338CA]" strokeWidth={1.75} />
        <h1 className="font-heading text-3xl font-bold tracking-tight">SMS Templates</h1>
      </div>
      <p className="text-sm text-[#52525B] max-w-2xl mb-8">
        Ready-to-send SMS messages for insurance, mutual funds, fees and subscriptions.
        Pick a client, copy the message, then send it from your own phone — no DLT registration
        or SMS gateway required.
      </p>

      {/* Client picker + search */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-4 mb-8">
        <div>
          <div className="co-label mb-2">Preview with client</div>
          <Select value={selectedClient || ""} onValueChange={setSelectedClient}>
            <SelectTrigger data-testid={T.smsClientPicker}>
              <SelectValue placeholder={clients.length === 0 ? "No clients yet" : "Choose a client"} />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name} — {c.product_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {currentClient && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-[#52525B]">
              <User className="h-3 w-3" strokeWidth={2} />
              <span>{currentClient.phone}</span>
            </div>
          )}
        </div>

        <div>
          <div className="co-label mb-2">Search templates</div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" strokeWidth={2} />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. insurance, gym, coaching..."
              className="pl-9"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#52525B]">Loading templates…</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-sm text-[#52525B]">No templates match your search.</div>
      ) : (
        <div className="space-y-10">
          {Object.entries(grouped).map(([cat, list]) => (
            <section key={cat}>
              <div className="co-label mb-3">{cat}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {list.map((t) => {
                  const r = rendered[t.id];
                  const previewText = r?.text || t.body;
                  const chars = r?.char_count ?? previewText.length;
                  const segs = r?.segments ?? (chars <= 160 ? 1 : Math.ceil(chars / 153));
                  return (
                    <div
                      key={t.id}
                      data-testid={T.smsTemplateCard}
                      className="border co-hairline rounded-md p-5 bg-white flex flex-col gap-3 hover:border-[#C7D2FE] transition-colors"
                    >
                      <div>
                        <div className="font-heading font-bold text-[#0A0A0B]">{t.name}</div>
                        <div className="text-xs text-[#52525B] mt-0.5">{t.description}</div>
                      </div>
                      <pre
                        data-testid={T.smsRenderedText}
                        className="whitespace-pre-wrap text-[13px] leading-relaxed bg-[#FAFAFA] border co-hairline p-3 rounded-sm font-mono text-[#18181B]"
                      >
                        {previewText}
                      </pre>
                      <div className="flex items-center justify-between text-[11px] text-[#71717A]">
                        <span>{chars} chars · {segs} SMS segment{segs > 1 ? "s" : ""}</span>
                        {!selectedClient && (
                          <span className="text-[#B45309]">Pick a client to fill placeholders</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <button
                          data-testid={T.smsCopyBtn}
                          onClick={() => copyText(previewText)}
                          className="co-btn-press inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[#4338CA] text-white rounded-sm hover:bg-[#3730A3]"
                        >
                          <Copy className="h-3.5 w-3.5" strokeWidth={2} />
                          Copy SMS
                        </button>
                        {selectedClient && currentClient && (
                          <button
                            data-testid={T.smsOpenBtn}
                            onClick={() => openSms(currentClient, previewText)}
                            title="Opens your phone's Messages app with this SMS pre-filled"
                            className="co-btn-press inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border co-hairline rounded-sm hover:bg-[#F4F4F5]"
                          >
                            <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
                            Open in SMS app
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
